import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { AppTable } from '@pro-sto/ui';
import type { AppTableColumn } from '@pro-sto/ui';
import { clientDetailStore } from '@/stores/ClientDetailStore';
import type { ClientBooking, AuditLogEntry, ClientDetailTab } from '@/stores/ClientDetailStore';
import './ClientDetailPage.css';

function formatPhone(phone: unknown): string {
  const raw = typeof phone === 'string' ? phone : String(phone ?? '');
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  }
  if (digits.length === 10) {
    return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
  }
  return raw || '—';
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_LABELS: Record<string, string> = {
  pending_confirmation: 'Ожидает',
  confirmed: 'Подтверждено',
  completed: 'Завершено',
  cancelled: 'Отменено',
};

const ACTION_LABELS: Record<string, string> = {
  client_register: 'Регистрация',
  client_login: 'Вход',
  client_set_phone: 'Смена телефона',
  client_refresh_token: 'Обновление токена',
  client_update_profile: 'Обновление профиля',
  client_delete_account: 'Удаление аккаунта',
  client_create_car: 'Добавление авто',
  client_update_car: 'Обновление авто',
  client_delete_car: 'Удаление авто',
  booking_create: 'Создание бронирования',
  booking_cancel: 'Отмена бронирования',
  payment_init: 'Инициализация оплаты',
  payment_status_update: 'Обновление статуса оплаты',
  payment_cancel: 'Отмена оплаты',
  payment_success: 'Успешная оплата',
  payment_failed: 'Неудачная оплата',
};

const TABS: { id: ClientDetailTab; label: string }[] = [
  { id: 'bookings', label: 'Бронирования' },
  { id: 'logs', label: 'Лог действий' },
];

/* ────── Bookings tab ────── */
const BookingsTab: React.FC<{ uuid: string }> = observer(({ uuid }) => {
  useEffect(() => {
    clientDetailStore.loadBookings(uuid);
  }, [uuid]);

  const columns: AppTableColumn<ClientBooking>[] = [
    {
      label: 'Дата',
      width: '22%',
      render: (row) => formatDateTime(row.start_time),
    },
    {
      label: 'Услуга',
      width: '28%',
      render: (row) => row.service?.name ?? '—',
    },
    {
      label: 'Сервис',
      width: '22%',
      render: (row) => row.service_center?.name ?? '—',
    },
    {
      label: 'Статус',
      width: '14%',
      render: (row) => (
        <span className={`client-detail-page__status client-detail-page__status_${row.status}`}>
          {STATUS_LABELS[row.status] ?? row.status}
        </span>
      ),
    },
    {
      label: 'Сумма',
      width: '14%',
      align: 'right',
      render: (row) => `${Number(row.total_cost).toLocaleString('ru-RU')} ₽`,
    },
  ];

  if (clientDetailStore.isLoadingBookings) {
    return <div className="client-detail-page__loading">Загрузка...</div>;
  }

  return (
    <AppTable<ClientBooking>
      columns={columns}
      rows={clientDetailStore.bookings}
      getRowKey={(row) => row.uuid}
      emptyPlaceholder="Нет бронирований"
    />
  );
});

/* ────── Audit logs tab ────── */
const LogsTab: React.FC<{ uuid: string }> = observer(({ uuid }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clientDetailStore.loadLogs(uuid);
  }, [uuid]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !clientDetailStore.isLoadingLogs && clientDetailStore.logsHasMore) {
          clientDetailStore.loadMoreLogs(uuid);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [uuid]);

  const columns: AppTableColumn<AuditLogEntry>[] = [
    {
      label: 'Дата',
      width: '25%',
      render: (row) => formatDateTime(row.created_at),
    },
    {
      label: 'Действие',
      width: '30%',
      render: (row) => ACTION_LABELS[row.action] ?? row.action,
    },
    {
      label: 'Сущность',
      width: '20%',
      render: (row) => row.entity_type ?? '—',
    },
    {
      label: 'UUID сущности',
      width: '25%',
      render: (row) => row.entity_uuid ? <span className="client-detail-page__uuid">{row.entity_uuid}</span> : '—',
    },
  ];

  if (clientDetailStore.isLoadingLogs && clientDetailStore.logs.length === 0) {
    return <div className="client-detail-page__loading">Загрузка...</div>;
  }

  return (
    <>
      <AppTable<AuditLogEntry>
        columns={columns}
        rows={clientDetailStore.logs}
        getRowKey={(row) => row.uuid}
        emptyPlaceholder="Нет записей"
      />
      <div ref={sentinelRef} className="client-detail-page__load-trigger" />
      {clientDetailStore.isLoadingLogs && clientDetailStore.logs.length > 0 && (
        <div className="client-detail-page__loading-more">
          <div className="client-detail-page__spinner" />
        </div>
      )}
    </>
  );
});

/* ────── Main page ────── */
const ClientDetailPage: React.FC = observer(() => {
  const { uuid } = useParams<{ uuid: string }>();

  useEffect(() => {
    if (!uuid) return;
    clientDetailStore.reset();
    clientDetailStore.loadClient(uuid);
  }, [uuid]);

  const { client, isLoadingClient, activeTab } = clientDetailStore;

  if (isLoadingClient || !client) {
    return (
      <div className="client-detail-page" data-testid="client-detail-page">
        <div className="client-detail-page__loading">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="client-detail-page" data-testid="client-detail-page">
      {/* Header */}
      <div className="client-detail-page__header">
        <h2 className="client-detail-page__name">
          {(client.name as unknown as string) || 'Без имени'}
        </h2>
        <span className="client-detail-page__phone">{formatPhone(client.phone)}</span>
      </div>

      {/* Tabs */}
      <div className="client-detail-page__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`client-detail-page__tab${activeTab === tab.id ? ' client-detail-page__tab_active' : ''}`}
            onClick={() => clientDetailStore.setTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="client-detail-page__content">
        {activeTab === 'bookings' && <BookingsTab uuid={uuid!} />}
        {activeTab === 'logs' && <LogsTab uuid={uuid!} />}
      </div>
    </div>
  );
});

export default ClientDetailPage;
